/**
 * OnboardPage — the landing target for the `hotline-widget` skill's prefill
 * deep-link (`/onboard?spec=<base64url>`). PUBLIC route.
 *
 * The `spec` is UNTRUSTED prefill (see lib/onboard-spec.ts): it only pre-populates
 * an editable form that the authenticated user reviews and confirms. Nothing is
 * created by the link alone.
 *
 * Flow: decode + show "what we'll create" (editable) → authenticate (register a
 * new org, or sign in) → create the widget instance via the existing
 * `POST /admin/widget-instances` → reveal the public key (to paste back into the
 * terminal / skill) + the one-time secret. No new backend endpoints.
 */
import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CAPTURE_LEVELS, type CaptureLevel } from '@hotline/contracts';

import { useAuth } from '../auth/AuthContext.js';
import { canonicalizeAllowedOrigin, validateAllowedOrigin } from '../lib/allowed-origin.js';
import { safeDecodeSpec } from '../lib/onboard-spec.js';
import { toMessage } from '../lib/useAsync.js';
import { StringListEditor } from '../components/StringListEditor.js';
import { CopyBlock, Field } from '../components/ui.js';
import { Alert, Button, Card, Input, Select, Spinner } from '../components/ui/index.js';

/** Short human labels for the four capture levels. */
const CAPTURE_LABELS: Record<CaptureLevel, string> = {
  0: 'L0 — Text only',
  1: 'L1 — + Diagnostics (screenshot, errors, console)',
  2: 'L2 — + Action timeline',
  3: 'L3 — + Session replay',
};

/** The one-time reveal after an instance is created. */
interface Revealed {
  id: string;
  publicKey: string;
  secretKey: string;
}

export default function OnboardPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const { status, user, register, login, client } = useAuth();

  const specToken = searchParams.get('spec');
  const decoded = useMemo(() => safeDecodeSpec(specToken), [specToken]);
  const specInvalid = specToken != null && !decoded.ok;
  const spec = decoded.ok ? decoded.spec : null;

  // Editable "what we'll create" state, seeded from the (untrusted) spec.
  const [instanceName, setInstanceName] = useState(spec?.name ?? '');
  const [orgName, setOrgName] = useState(spec?.organizationName ?? '');
  const [origins, setOrigins] = useState<string[]>(spec?.allowedOrigins ?? []);
  const [captureLevel, setCaptureLevel] = useState<CaptureLevel>(spec?.captureLevel ?? 1);

  // Anonymous auth sub-form.
  const [authMode, setAuthMode] = useState<'register' | 'signin'>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);

  const authenticated = status === 'authenticated';

  /** Create the instance with the current (edited) form values. */
  const createInstance = async (): Promise<void> => {
    const name = instanceName.trim();
    if (!name) {
      throw new Error('Give the widget instance a name first.');
    }
    const created = await client.api.createWidgetInstance({
      name,
      captureLevel,
      allowedOrigins: origins,
    });
    setRevealed({
      id: created.id,
      publicKey: created.publicKey,
      secretKey: created.secretKey ?? '',
    });
  };

  /** Authenticated user confirms → create. */
  const onCreate = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await createInstance();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  /** Anonymous user: register a new org (or sign in), then create in one flow. */
  const onAuthSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!instanceName.trim()) {
      setError('Give the widget instance a name first.');
      return;
    }
    setSubmitting(true);
    try {
      if (authMode === 'register') {
        await register({
          email,
          password,
          ...(orgName.trim() ? { organizationName: orgName.trim() } : {}),
        });
      } else {
        await login({ email, password });
      }
      await createInstance();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="grid min-h-screen place-items-center p-6"
      style={{
        background:
          'radial-gradient(120% 90% at 50% -10%, var(--color-primary-soft), var(--color-bg) 60%)',
      }}
    >
      <div className="w-full max-w-[520px]">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden="true"
            className="grid h-[46px] w-[46px] place-items-center rounded-[14px] bg-primary shadow-[0_0_28px_-4px_var(--hl-primary)]"
          >
            <span className="h-[13px] w-[13px] rounded-full bg-white motion-safe:animate-pulse" />
          </span>
          <div>
            <h1 className="text-[17px] font-extrabold tracking-[0.16em] text-text">
              SET UP YOUR WIDGET
            </h1>
            <p className="mt-1 text-[13px] text-muted">
              Create a Hotline widget instance{spec?.framework ? ` for your ${spec.framework} app` : ''} and get its public key.
            </p>
          </div>
        </div>

        {revealed ? (
          <Card>
            <Card.Body className="flex flex-col gap-4">
              <div>
                <h2 className="text-sm font-bold text-text">Your widget instance is ready 🎉</h2>
                <p className="mt-1 text-xs text-muted">
                  Copy the <strong>public key</strong> and paste it back into your terminal
                  (the Hotline skill will wire it into your app). It’s safe in the browser.
                </p>
              </div>
              <CopyBlock label="Public key — paste this back" code={revealed.publicKey} />
              {revealed.secretKey ? (
                <div className="rounded-md border border-warn/40 bg-warn-soft p-3">
                  <p className="mb-2 text-xs font-semibold text-text">
                    Secret key — shown only once, keep it server-side. Never put it in the browser.
                  </p>
                  <CopyBlock label="Secret key" code={revealed.secretKey} />
                </div>
              ) : null}
              <p className="rounded-md border border-border bg-surface-2 px-3 py-2 text-xs text-muted">
                Confirm your site’s origin(s) are on this instance’s allowlist:{' '}
                <span className="font-mono text-text">
                  {origins.length ? origins.join(', ') : '(none set yet)'}
                </span>
                . Changes take ~30s to propagate.
              </p>
              <div className="flex gap-2">
                <Link
                  to={`/org/instances/${revealed.id}`}
                  className="inline-flex h-[27px] items-center gap-[7px] rounded-sm border border-border bg-surface-2 px-2.5 text-xs font-semibold text-text transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Configure instance
                </Link>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body className="flex flex-col gap-4">
              {specInvalid ? (
                <Alert>
                  This link’s prefill couldn’t be read — fill the details in below.
                </Alert>
              ) : null}

              <Field label="Widget instance name" htmlFor="wi-name">
                <Input
                  id="wi-name"
                  aria-label="Widget instance name"
                  placeholder="My app"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value)}
                  required
                />
              </Field>

              <Field
                label="Allowed origins"
                hint="Exact scheme+host+port. The widget is rejected (403) from origins not listed here."
              >
                <StringListEditor
                  label="Add an allowed origin"
                  placeholder="https://example.com"
                  values={origins}
                  onChange={setOrigins}
                  validate={validateAllowedOrigin}
                  normalize={canonicalizeAllowedOrigin}
                />
              </Field>

              <Field label="Capture level" htmlFor="wi-level">
                <Select
                  id="wi-level"
                  aria-label="Capture level"
                  value={captureLevel}
                  onChange={(e) => setCaptureLevel(Number(e.target.value) as CaptureLevel)}
                >
                  {CAPTURE_LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {CAPTURE_LABELS[lvl]}
                    </option>
                  ))}
                </Select>
              </Field>

              {error ? <Alert>{error}</Alert> : null}

              {authenticated ? (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-muted">
                    Signed in{user?.email ? ` as ${user.email}` : ''}. This instance will be
                    created in your organization.
                  </p>
                  <Button type="button" size="block" onClick={onCreate} disabled={submitting}>
                    {submitting ? <Spinner size="sm" label="Creating…" /> : 'Create widget instance'}
                  </Button>
                </div>
              ) : (
                <form className="flex flex-col gap-3 border-t border-border pt-4" onSubmit={onAuthSubmit}>
                  <div className="flex items-center gap-2 text-xs font-semibold">
                    <button
                      type="button"
                      className={authMode === 'register' ? 'text-text' : 'text-faint'}
                      onClick={() => setAuthMode('register')}
                    >
                      Create account
                    </button>
                    <span className="text-faint">·</span>
                    <button
                      type="button"
                      className={authMode === 'signin' ? 'text-text' : 'text-faint'}
                      onClick={() => setAuthMode('signin')}
                    >
                      Sign in
                    </button>
                  </div>

                  {authMode === 'register' ? (
                    <Field label="Organization name" htmlFor="org-name">
                      <Input
                        id="org-name"
                        aria-label="Organization name"
                        placeholder="Your company"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                      />
                    </Field>
                  ) : null}

                  <Field label="Email" htmlFor="email">
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Password"
                    htmlFor="password"
                    hint={authMode === 'register' ? 'At least 8 characters.' : undefined}
                  >
                    <Input
                      id="password"
                      type="password"
                      autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                      required
                      minLength={authMode === 'register' ? 8 : undefined}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Field>

                  <Button type="submit" size="block" disabled={submitting}>
                    {submitting ? (
                      <Spinner size="sm" label="Working…" />
                    ) : authMode === 'register' ? (
                      'Create account & widget'
                    ) : (
                      'Sign in & create widget'
                    )}
                  </Button>
                  <p className="text-center text-[11px] text-faint">
                    More sign-in options on the{' '}
                    <Link className="font-semibold text-muted hover:text-text" to="/login">
                      login page
                    </Link>
                    .
                  </p>
                </form>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
}
